/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { isSeverityIconNeeded, MessageStartIcon, MessageSummary } from '../PRIVATE_Message';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { ComponentMessageVariantOptions } from './themes/ComponentMessageStyles.css';
import { ComponentMessageRedwoodTheme } from './themes/redwood/ComponentMessageTheme';
import { classNames } from '#utils/UNSAFE_classNames';
import { type TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { UserAssistanceRedwoodTheme } from '#UNSAFE_UserAssistance/themes/redwood/UserAssistanceTheme';
import { getClientHints } from '#utils/PRIVATE_clientHints';
/**
 * A type for a single component message
 */
export type ComponentMessageItem = {
  summary?: string;
  detail?: string;
  severity?: 'confirmation' | 'error' | 'info' | 'warning';
};

/**
 * Props for the Message Component
 */
type Props = TestIdProps & {
  /**
   * The error detail text for the component message
   */
  detail?: ComponentMessageItem['detail'];

  /**
   * The label of the field which is showing this error
   */
  fieldLabel?: string;

  /**
   * The severity of the component message
   */
  severity?: ComponentMessageItem['severity'];
};

/**
 * The component that renders an individual message for inline component messaging.
 */
export function ComponentMessage({ detail, fieldLabel, severity = 'error', testId }: Props) {
  const { classes } = useComponentTheme<ComponentMessageVariantOptions>(
    ComponentMessageRedwoodTheme,
    {
      severity: severity ?? 'none'
    }
  );

  const { baseTheme } = useComponentTheme(UserAssistanceRedwoodTheme);
  const styleClasses = classNames([classes, baseTheme]);
  const testIdProps = useTestId(testId);

  // aria-live assertive works on ios VoiceOver, but not on Jaws for our non-input-element form components like checkbox-set and input-date-mask,
  // and role='alert' works on Jaws but not ios Voiceover due to VO bug.
  // JET-65574 error message is not read on focus in voiceover due to VoiceOver bug with role: 'alert'.
  // JET-72375 Accessibility - Checkbox, Error message is not read by JAWS due to aria-live: 'assertive'.

  return (
    <div
      class={styleClasses}
      {...(isIosSafari() ? { 'aria-live': 'assertive' } : { role: 'alert' })}
      aria-atomic="true"
      {...testIdProps}>
      {fieldLabel && <HiddenAccessible>{fieldLabel}</HiddenAccessible>}
      {isSeverityIconNeeded(severity) && (
        <MessageStartIcon
          severity={severity}
          testId={testId ? `${testId}-icon` : undefined}
          variant="inline"
        />
      )}
      {/* We will be using the MessageSummary component, as this aligns with the style required */}
      <MessageSummary
        testId={testId ? `${testId}-summary` : undefined}
        text={detail}
        variant="inline"
      />
    </div>
  );
}

/**
 * Helper function to determine if the platform is iOS and the Browser is Safari.
 * Exported for usage in local tests.
 * @returns true if the platform is iOS, false otherwise
 */
export function isIosSafari() {
  return getClientHints().platform === 'ios' && getClientHints().browser === 'safari';
}
